import { useId, useState, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface SwitchProps extends Omit<ComponentPropsWithRef<"button">, "onChange" | "value"> {
  /** Mode contrôlé. Laissez vide pour un fonctionnement autonome. */
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  size?: "sm" | "md";
}

/**
 * Interrupteur.
 *
 * Différence avec `Checkbox`, qui décide de son usage : une case à cocher
 * exprime une sélection qui sera validée plus tard (formulaire), un interrupteur
 * applique son effet immédiatement. S'il faut cliquer « Enregistrer » ensuite,
 * c'est une case à cocher.
 *
 * Bâti sur un `<button role="switch">` : le rôle natif n'existe pas en HTML,
 * mais il est parfaitement supporté par les lecteurs d'écran, contrairement à
 * une case à cocher détournée.
 */
export function Switch({
  className,
  checked,
  defaultChecked = false,
  onChange,
  label,
  description,
  size = "md",
  disabled,
  id,
  ...props
}: SwitchProps) {
  const generatedId = useId();
  const switchId = id ?? generatedId;

  const [internal, setInternal] = useState(defaultChecked);
  const isControlled = checked !== undefined;
  const isOn = isControlled ? checked : internal;

  const toggle = () => {
    if (!isControlled) setInternal(!isOn);
    onChange?.(!isOn);
  };

  const track = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const thumb = size === "sm" ? "size-4" : "size-5";
  const travel = size === "sm" ? "translate-x-4" : "translate-x-5";

  const control = (
    <button
      type="button"
      role="switch"
      id={switchId}
      aria-checked={isOn}
      disabled={disabled}
      onClick={toggle}
      className={cn(
        "group/switch relative inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 touch-manipulation",
        "before:absolute before:-inset-2.5 before:content-[''] sm:before:hidden",
        "transition-colors duration-normal ease-standard",
        "inset-shadow-control",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        "disabled:cursor-not-allowed disabled:opacity-50",
        track,
        isOn ? "bg-accent" : "bg-border-strong",
        className,
      )}
      {...props}
    >
      {/*
        Le pouce voyage avec la courbe `spring` : il dépasse très légèrement sa
        position finale avant de se caler. C'est ce dépassement qui fait qu'un
        interrupteur paraît mécanique plutôt que logiciel.
        Il s'étire aussi horizontalement pendant l'appui (`scale-x-110`), comme
        un objet physique entraîné par le doigt.
      */}
      <span
        className={cn(
          "rounded-full bg-surface-raised shadow-sm",
          // Trajet et étirement tiennent dans une seule matrice : le pouce ne
          // change jamais de boîte, il n'y a donc rien à remettre en page.
          "transform-gpu will-change-transform transition-transform duration-normal ease-spring",
          "group-active/switch:scale-x-110",
          thumb,
          isOn ? travel : "translate-x-0",
        )}
      />
    </button>
  );

  if (!label) return control;

  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex flex-col gap-0.5">
        <label htmlFor={switchId} className="cursor-pointer text-sm font-medium text-fg select-none">
          {label}
        </label>
        {description && <span className="text-xs text-fg-muted">{description}</span>}
      </span>
      {control}
    </div>
  );
}
